import {
  Box,
  Button,
  Container,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { HookedField } from "../../components/HookedField";
import { HookedForm } from "../../components/HookedForm";
import { RoutePaths } from "../../consts/enums";
import countries from "../../../common/assets/countries.json";
import { UserService } from "../../../common/services/UserService";
import { useAppContext } from "../../../common/context/AppContext";
import { OptionField } from "../../components/OptionField";
import { useState } from "react";
import { UserType } from "../../../common/types";
import { CartIcon } from "../../../common/assets/icons/Cart";
import { SellerIcon } from "../../../common/assets/icons/Seller";
import {
  LocalStorageKey,
  StorageService,
} from "../../../common/services/StorageService";

const TypeOfUser = [
  {
    Icon: CartIcon,
    label: "Buyer",
    value: UserType.Buyer,
    description: "I’m interested in buying a business",
  },
  {
    Icon: SellerIcon,
    label: "Seller",
    value: UserType.Seller,
    description: "I’m interested in selling a business",
  },
];

const schema = yup
  .object({
    active_role: yup.string().required("You must select a type").trim(),
    first_name: yup
      .string()
      .required()
      .nullable()
      .max(600)
      .trim()
      .label("First Name"),
    last_name: yup
      .string()
      .required()
      .nullable()
      .max(600)
      .trim()
      .label("Last Name"),
    country: yup
      .string()
      .required()
      .nullable()
      .max(600)
      .trim()
      .label("Country"),
    phone_number: yup
      .string()
      .required()
      .nullable()
      .max(600)
      .trim()
      .label("Phone Number"),
  })
  .required();

export const BasicInformation = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAppContext();
  const [userType, setUserType] = useState<UserType | undefined>( // eslint-disable-line @typescript-eslint/no-unused-vars
    (user.active_role as UserType) || undefined
  );

  const formHook = useForm({
    defaultValues: user,
    resolver: yupResolver(schema),
  });
  const getUserType = (v: UserType) => {
    setUserType(v);
  };

  const patchUser = async (values: any) => {
    const referralKey = StorageService.Get(LocalStorageKey.ReferralKeys);
    const res = await UserService.OnboardPatch({
      id: user.id,
      ...values,
      phone_number: values.country + values.phone_number,
      active_role: userType,
      ...(referralKey && { referral: { ...JSON.parse(referralKey) } }),
    });
    if (referralKey) {
      StorageService.Remove(LocalStorageKey.ReferralKeys);
    }
    updateUser((prev) => ({
      ...prev,
      ...res,
    }));
    navigate(RoutePaths.REGISTER + "/informations");
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <HookedForm formHook={formHook}>
        <Container
          maxWidth="sm"
          sx={{
            paddingTop: 13,
            gap: 3,
            // padding: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ color: "common.gray", marginBottom: 2 }}>
            <Typography fontWeight={700}>Private Information</Typography>
            <Typography>
              Other Foundy users will NOT be able to see this information
            </Typography>
          </Box>

          <OptionField
            question="What type of user you are?"
            name="active_role"
            options={TypeOfUser}
            onChange={(v) => getUserType(v as UserType)}
          />

          <Box
            sx={{
              width: "100%",
              flexDirection: "row",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              alignContent: "stretch",
              columnGap: 2,
            }}
          >
            <HookedField
              required
              sx={{ order: 0, flex: "2 1 auto" }}
              component={TextField}
              label="First Name"
              name="first_name"
              variant="filled"
            />
            <HookedField
              required
              component={TextField}
              sx={{ order: 1, flex: "1 0 auto" }}
              label="Last Name"
              name="last_name"
              variant="filled"
            />
          </Box>
          <Box
            sx={{
              width: "100%",
              flexDirection: "row",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              alignContent: "stretch",
              columnGap: 2,
            }}
          >
            <HookedField
              required
              name="country"
              component={TextField}
              select
              defaultValue="+44"
              variant="filled"
              label="Country"
              sx={{ order: 1, flex: "1 0 auto" }}
            >
              {countries.map((x) => (
                <MenuItem key={x.code} value={x.dial_code}>
                  {x.code ? (
                    <ListItemIcon sx={{ minWidth: 16, marginRight: 1 }}>
                      <img
                        src={`https://flagcdn.com/16x12/${x.code.toLocaleLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/32x24/${x.code.toLocaleLowerCase()}.png 2x,
    https://flagcdn.com/48x36/${x.code.toLocaleLowerCase()}.png 3x`}
                        width="16"
                        height="12"
                        alt={x.name}
                      />
                    </ListItemIcon>
                  ) : (
                    <ListItemIcon>{x.dial_code}</ListItemIcon>
                  )}
                  <ListItemText sx={{ display: "inline-flex", margin: 0 }}>
                    {x.name} {x.dial_code}
                  </ListItemText>
                </MenuItem>
              ))}
            </HookedField>
            <HookedField
              required
              component={TextField}
              variant="filled"
              name="phone_number"
              label="Phone Number"
              sx={{ order: 1, flex: "2 1 auto" }}
            />
          </Box>
          <Box sx={{ width: "100%" }}>
            <Button
              variant="roundprimary"
              sx={{ width: "max-content", backgroundColor: "common.lightgray" }}
              onClick={formHook.handleSubmit(patchUser)}
            >
              Continue
            </Button>
          </Box>
        </Container>
      </HookedForm>
    </Box>
  );
};
